import { fromJS, List } from 'immutable';
import get from 'lodash/get';

import { ApiActions } from '../../constants/ApiActions';
import { ListenerActions, ListenerKeys } from '../../constants/Listener';
import { SocialActions } from '../../constants/Social';
import { Map } from '../../lib/ImmutableUtils';
import { createReducer } from '../../lib/ReducerUtils';
import { type SetHasGivenFeedbackAction, type WithSharedState } from '../../types';

export const INITIAL_STATE = Map({
  [ListenerKeys.CURRENT_USER_ID_KEY]: null as string | $TSFixMe | null,
  [ListenerKeys.LISTENERS_KEY]: Map<$TSFixMe>({}),
});
export type EntitiesListenerState = typeof INITIAL_STATE;

// LISTENER REDUCER
export default createReducer(INITIAL_STATE, {
  [ApiActions.REGISTER_SUCCESS]: saveListenerData,
  [ApiActions.LOGIN_SUCCESS]: saveListenerData,
  [ApiActions.ANON_LOGIN_SUCCESS]: saveListenerData,
  [ApiActions.DEVICE_LOGIN_SUCCESS]: saveListenerData,
  [ApiActions.GET_PROFILE_SUCCESS]: updateListenerProfile,
  [ApiActions.SET_PROFILE_SUCCESS]: updateListenerProfile,
  [ApiActions.UPDATE_SETTINGS_SUCCESS]: updateListenerProfile,
  [ApiActions.UPDATE_ACCOUNT_SUCCESS]: updateListenerProfile,
  [ApiActions.ADD_FEEDBACK_SUCCESS]: addFeedbackSuccess,
  [ListenerActions.SET_HAS_GIVEN_FEEDBACK]: setHasGivenFeedback,
  [ApiActions.DELETE_FEEDBACK_SUCCESS]: deleteFeedbackSuccess,
  [ApiActions.ACTION_REMOVE_THUMB_SUCCESS]: deleteFeedbackSuccess,
  [ApiActions.FACEBOOK_CONNECT_SUCCESS]: updateFacebookData,
  [ApiActions.FACEBOOK_DISCONNECT_SUCCESS]: updateFacebookData,
  [SocialActions.SET_FACEBOOK_TOKEN_VALIDITY]: setFacebookTokenValidity,
  [SocialActions.REMOVE_FACEBOOK_DATA]: removeFacebookData,
  [ApiActions.SET_TWITTER_TOKENS_SUCCESS]: updateTwitterData,
  [ApiActions.GET_FRAGMENT_SUCCESS]: getFragmentSuccess,
  [ApiActions.LOGOUT_SUCCESS]: logoutSuccess,
  [ApiActions.ACQUIRE_PRODUCT_SUCCESS]: updateListenerSubscription,
  [ApiActions.GET_EXPERIMENTS_FOR_LISTENER_SUCCESS]: saveABExperimentsData,
});

// HELPER FUNCTIONS

function getPositiveThumbCount(
  isPositive: boolean,
  wasTrackThumbedUp: boolean,
  currentThumbCount: number,
): number {
  let positiveThumbCountDiff = 0;
  if (isPositive) {
    positiveThumbCountDiff = 1;
  }
  if (!isPositive && wasTrackThumbedUp) {
    positiveThumbCountDiff = -1;
  }
  return currentThumbCount + positiveThumbCountDiff;
}

// CASE FUNCTIONS

function saveListenerData(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = get(action, 'response.result', null);
  const listenerData = action.response.entities.listeners[listenerId];
  listenerData.isAnonymous =
    action.type === ApiActions.ANON_LOGIN_SUCCESS && !action.isCustomerServiceLogin;
  return mergeListenerData(state, listenerId, listenerData);
}

function saveABExperimentsData(
  state: EntitiesListenerState,
  action: $TSFixMe,
): EntitiesListenerState {
  const listenerId = get(action, 'sharedState.listenerId', null);
  if (!listenerId) return state;

  const listenerExperiments = action.response.map(
    (obj: $TSFixMe) => obj.treatmentArm && obj.treatmentArm.id,
  );
  return state.updateIn(
    [ListenerKeys.LISTENERS_KEY, listenerId, ListenerKeys.CONFIG, ListenerKeys.EXPERIMENTS],
    // @ts-expect-error
    experiments => List(experiments).concat(listenerExperiments),
  );
}

function updateListenerProfile(
  state: EntitiesListenerState,
  action: $TSFixMe,
): EntitiesListenerState {
  return state.mergeDeepIn([ListenerKeys.LISTENERS_KEY], action.response.entities.listeners);
}

function updateListenerSubscription(
  state: EntitiesListenerState,
  action: $TSFixMe,
): EntitiesListenerState {
  const listenerId = action.response.result;
  return state.setIn(
    [ListenerKeys.LISTENERS_KEY, listenerId],
    fromJS({
      ...action.response.entities.listeners[listenerId],
    }),
  );
}

function addFeedbackSuccess(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  const isPositive = action.isPositive;
  const wasTrackThumbedUp = action.wasTrackThumbedUp;
  const currentThumbCount =
    state.getIn([ListenerKeys.LISTENERS_KEY, listenerId, 'positiveFeedbackCount']) || 0;

  return state.setIn(
    [ListenerKeys.LISTENERS_KEY, listenerId, 'positiveFeedbackCount'],
    getPositiveThumbCount(isPositive, wasTrackThumbedUp, currentThumbCount),
  );
}

function setHasGivenFeedback(
  state: EntitiesListenerState,
  action: WithSharedState<SetHasGivenFeedbackAction>,
): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  const isPositive = action.payload.isPositive;

  if (isPositive) {
    return state.setIn([ListenerKeys.LISTENERS_KEY, listenerId, 'hasGivenPositiveFeedback'], true);
  }
  return state.setIn([ListenerKeys.LISTENERS_KEY, listenerId, 'hasGivenNegativeFeedback'], true);
}

function deleteFeedbackSuccess(
  state: EntitiesListenerState,
  action: $TSFixMe,
): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  const isPositive = action.isPositive;
  const updatedThumbCount =
    state.getIn([ListenerKeys.LISTENERS_KEY, listenerId, 'positiveFeedbackCount']) || 0;

  if (isPositive) {
    return state.setIn(
      [ListenerKeys.LISTENERS_KEY, listenerId, 'positiveFeedbackCount'],
      updatedThumbCount - 1,
    );
  }
  return state;
}

function updateFacebookData(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  const facebookData = action.payload;

  return state.setIn([ListenerKeys.LISTENERS_KEY, listenerId, 'facebookData'], Map(facebookData));
}

function removeFacebookData(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;

  return state.setIn([ListenerKeys.LISTENERS_KEY, listenerId, 'facebookData'], Map());
}

function setFacebookTokenValidity(
  state: EntitiesListenerState,
  { payload: { listenerId, valid } }: $TSFixMe,
): EntitiesListenerState {
  return state.setIn(
    [ListenerKeys.LISTENERS_KEY, listenerId, ListenerKeys.IS_FACEBOOK_TOKEN_VALID],
    valid,
  );
}

function updateTwitterData(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  const twitterData = action.payload;

  return state.setIn([ListenerKeys.LISTENERS_KEY, listenerId, 'twitterData'], Map(twitterData));
}

function getFragmentSuccess(state: EntitiesListenerState, action: $TSFixMe): EntitiesListenerState {
  const listenerId = action.sharedState.listenerId;
  return state.setIn(
    [ListenerKeys.LISTENERS_KEY, listenerId, 'isBingeSkipping'],
    !!action.response.result.isBingeSkipping,
  );
}

function logoutSuccess(state: EntitiesListenerState): EntitiesListenerState {
  return state.set(
    ListenerKeys.CURRENT_USER_ID_KEY,
    INITIAL_STATE.get(ListenerKeys.CURRENT_USER_ID_KEY),
  );
}

function mergeListenerData(
  state: EntitiesListenerState,
  listenerId: string,
  listenerData: $TSFixMe,
): EntitiesListenerState {
  return state.withMutations(map => {
    map
      .set(ListenerKeys.CURRENT_USER_ID_KEY, listenerId)
      .setIn([ListenerKeys.LISTENERS_KEY, listenerId], fromJS(listenerData));
  });
}
